import type { HandlerOptions } from 'miragejs/server'
import type { Request, Registry } from 'miragejs'
import type {
  AnyModels,
  AnyFactories,
  FactoryDefinition,
  ModelDefinition,
} from 'miragejs/-types'
import type Schema from 'miragejs/orm/schema'
import type { HTTPMethod } from '@/api/types.js'
import {
  entitiesWithRoutes,
  type EntityWithRoutes,
  type MockServer,
} from '@/api/mock-server/server.js'

/**
 * Static imports for production builds
 *
 * Dynamic imports with computed paths don't work in production because bundlers
 * can't statically analyze the import path at build time. If the bundler can't
 * determine which files might be imported, it won't include them in the production
 * build at all, causing runtime import failures.
 *
 * Static imports solve this by allowing the bundler to see exactly which files
 * are needed and ensuring they're included in the production build.
 */
export type ExtendedHandlerOptions = HandlerOptions & {
  seed?: EntityWithRoutes | EntityWithRoutes[]
}

/**
 * Lazy seeding composable that creates data only when specific endpoints are accessed
 */
export const useLazySeedingManager = (server: MockServer) => {
  const seededEntities = new Set<EntityWithRoutes>()
  const ongoingSeedingOperations = new Map<EntityWithRoutes, Promise<void>>()
  const seedingStartTimes = new Map<EntityWithRoutes, number>()
  const isSeeding = useState('isLazySeeding', () => false)
  const currentSeedName = useState('currentSeedName', () => '')
  const seedingStartTime = ref<number | null>(null)

  /**
   * Start seeding state with minimum 1-second visibility guarantee
   */
  const _setIsSeeding = (entityType?: EntityWithRoutes) => {
    if (isSeeding.value) {
      return
    }

    isSeeding.value = true
    if (entityType) {
      currentSeedName.value = entityType
    }
    seedingStartTime.value = Date.now()
  }

  /**
   * Stop seeding state. A minimum duration can be specified to ensure that the
   * seeding state is visible for a minimum amount of time.
   * Default value is 1 second.
   */
  const _unsetIsSeeding = (minDurationMs = 1000) => {
    if (ongoingSeedingOperations.size === 0 && seedingStartTime.value) {
      const elapsed = Date.now() - seedingStartTime.value

      if (elapsed < minDurationMs) {
        const { start } = useTimeoutFn(() => {
          isSeeding.value = false
          currentSeedName.value = ''
          seedingStartTime.value = null
        }, minDurationMs - elapsed)

        start()
      } else {
        isSeeding.value = false
        currentSeedName.value = ''
        seedingStartTime.value = null
      }
    }
  }

  /**
   * Mark an entity type as seeded to avoid seeding it more than once
   */
  const _markAsSeeded = (entityType: EntityWithRoutes) => {
    seededEntities.add(entityType)
  }

  /**
   * Dynamically import a seeding module
   */
  const _importSeedingModule = async (entityType: EntityWithRoutes) => {
    try {
      return await entitiesWithRoutes?.[entityType]
    } catch (error) {
      console.error(`Error importing seeding module for ${entityType}:`, error)
      return null
    }
  }

  /**
   * Internal method to perform the actual seeding
   */
  const _performSeeding = async (entityType: EntityWithRoutes) => {
    // Double-check in case another operation completed while we were waiting
    if (isSeeded(entityType)) {
      console.log(`🔄 Entity ${entityType} already seeded, skipping`)
      return
    }

    const startTime = Date.now()
    seedingStartTimes.set(entityType, startTime)

    try {
      const seedingModule = ((await _importSeedingModule(entityType)) ||
        {}) as {
        seeds?: (server: MockServer) => Promise<void>
      }

      // Some modules don't have seeds. Skip the seeding if that's the case.
      if (!seedingModule?.seeds) {
        return
      }

      await seedingModule.seeds(server)
      _markAsSeeded(entityType)

      const endTime = Date.now()
      const duration = endTime - startTime

      const durationThreshold = 1000
      const durationColor = duration > durationThreshold ? '#dc2626' : '#0c1a3d'
      const durationBackground =
        duration > durationThreshold ? '#fef2f2' : '#fff'

      const collectionsWithData = Object.fromEntries(
        Object.entries(server.db._collections).filter(
          ([, collection]) => collection._records?.length,
        ),
      )

      console.log(
        `🌱 %c Lazy seeded %c ${entityType} %c ${duration}ms`,
        'background: #ebf9eb; color: #1d8633; font-weight: bold; padding: 2px 0; border-radius: 4px 0 0 4px;',
        'background: #b7dfba; color: #0c1a3d; padding: 2px;',
        `background: ${durationBackground}; color: ${durationColor}; padding: 2px 8px 2px 0; border-radius: 0 4px 4px 0;`,
        {
          db: collectionsWithData,
        },
      )
    } catch (error) {
      const endTime = Date.now()
      const duration = endTime - startTime

      console.error(
        `❌ Error during seeding for ${entityType} (${duration}ms):`,
        error,
      )
    } finally {
      seedingStartTimes.delete(entityType)
    }
  }

  /**
   * Creates a method wrapper that adds seeding functionality to an HTTP method
   * by adding a seeding option to the handler options.
   *
   * @param server - The Mirage server instance
   * @param originalMethod - The original method to extend
   * @returns A new method that adds seeding functionality to the original method
   */
  const _extendMethodWithSeeding = <TServer extends MockServer>(
    server: TServer,
    originalMethod: TServer[HTTPMethod],
  ) => {
    return function (
      this: TServer,
      path: Parameters<typeof originalMethod>[0],
      handler?: Parameters<typeof originalMethod>[1],
      options?: ExtendedHandlerOptions,
    ): ReturnType<typeof originalMethod.call> {
      const seed = options?.seed
      if (!seed) {
        // If no seed option is provided, use the original method
        return originalMethod.call(server, path, handler, options || {})!
      }

      // Create a wrapper handler that performs seeding before executing the original handler
      const customizedHandler = async function (
        this: TServer,
        schema: Schema<Registry<AnyModels, AnyFactories>>,
        request: Request,
      ) {
        if (Array.isArray(seed)) {
          await Array.fromAsync(seed, (entityType) =>
            server?.lazySeedingManager?.seedBasic(entityType),
          )
        } else {
          await server?.lazySeedingManager?.seedBasic(seed)
        }

        // Execute the original handler
        return handler?.call?.(this, schema, request)
      }

      // Extract the original options and call original method with them
      const { seed: _, ...routeHandlerOptions } = options || {}
      return originalMethod.call(
        server,
        path,
        customizedHandler,
        routeHandlerOptions,
      )
    }
  }

  /**
   * Helper to extend a method with auto-seeding functionality
   */
  function _extendMethodWithAutoSeeding<
    Method extends keyof Pick<MockServer, HTTPMethod>,
  >(
    method: Method,
    entityType: EntityWithRoutes,
    routeHandlers: Pick<MockServer, Method>,
    entityRoutes: string[],
  ) {
    return function (
      this: MockServer,
      path: Parameters<MockServer[Method]>[0],
      handler?: Parameters<MockServer[Method]>[1],
      options?: ExtendedHandlerOptions,
    ) {
      entityRoutes.push(path as string)

      // Combine the seed that is automatically provided by the route handler
      // with the seed that is manually provided in the route handler options
      // through the `seed` option (e.g. `{ seed: ['Client', 'Patient'] }`)
      const existingSeed = options?.seed
      const existingSeedArray = Array.isArray(existingSeed)
        ? existingSeed
        : [existingSeed]
      const combinedSeed = [entityType, ...existingSeedArray].filter(Boolean)

      const enhancedOptions = { ...options, seed: combinedSeed }
      return routeHandlers[method].call(this, path, handler, enhancedOptions)
    }
  }

  /**
   * Get list of all entities that have already been seeded
   */
  const getSeededEntities = () => {
    return Array.from(seededEntities)
  }

  /**
   * Check if an entity type has been seeded
   */
  const isSeeded = (entityType: EntityWithRoutes) => {
    return seededEntities.has(entityType)
  }

  /**
   * Seed basic data for the specified entity type
   * Deduplicates concurrent calls for the same entity type so that
   * we don't seed the same entity type multiple times.
   */
  const seedBasic = async (entityType: EntityWithRoutes) => {
    if (isSeeded(entityType)) {
      return
    }

    // Check if there's already an ongoing seeding operation for this entity type
    const ongoingOperation = ongoingSeedingOperations.get(entityType)
    if (ongoingOperation) {
      // Wait for the existing operation to complete
      await ongoingOperation
      return
    }

    // Start seeding state management
    _setIsSeeding(entityType)

    // Create and store the seeding operation
    const seedingOperation = _performSeeding(entityType)
    ongoingSeedingOperations.set(entityType, seedingOperation)

    try {
      await seedingOperation
    } finally {
      // Clean up the ongoing operation reference
      ongoingSeedingOperations.delete(entityType)
      _unsetIsSeeding()
    }
  }

  /**
   * Sets auto-seeding to the routes for the specified entity type
   */
  const setAutoSeedingToRoutes = (
    entityType: EntityWithRoutes,
    module: {
      model?: ModelDefinition<AnyModels>
      factory?: FactoryDefinition<AnyFactories>
      routes?: (server: MockServer) => void
      seeds?: (server: MockServer) => void
    },
    server: MockServer,
  ) => {
    const entityRoutes: string[] = []

    // Get the route handlers that will be used to track the routes
    // e.g. get, post, patch, delete, etc.
    const routeHandlers = server.lazySeedingManager!.routeHandlers
    const routeHandlerMethodNames = Object.keys(routeHandlers) as HTTPMethod[]

    // Store the original methods before we modify them so we can restore them later
    const originalMethods = routeHandlerMethodNames.reduce(
      (acc, type) => {
        acc[type] = server[type].bind(server)
        return acc
      },
      {} as Record<HTTPMethod, MockServer[HTTPMethod]>,
    )

    // Add our modified route handlers to the server
    Object.assign(
      server,
      routeHandlerMethodNames.reduce(
        (acc, methodName) => {
          acc[methodName] = _extendMethodWithAutoSeeding(
            methodName,
            entityType,
            routeHandlers,
            entityRoutes,
          )
          return acc
        },
        {} as Record<HTTPMethod, MockServer[HTTPMethod]>,
      ),
    )

    // Initialize the routes for the entity with our modified methods
    // that contain auto-seeding.
    module.routes?.(server)

    // Now restore the server to its original state
    Object.assign(server, originalMethods)
  }

  const routeHandlers = {
    get: _extendMethodWithSeeding(server, server.get),
    post: _extendMethodWithSeeding(server, server.post),
    patch: _extendMethodWithSeeding(server, server.patch),
    put: _extendMethodWithSeeding(server, server.put),
    delete: _extendMethodWithSeeding(server, server.delete),
  } as const

  return {
    isSeeded,
    seedBasic,
    getSeededEntities,
    setAutoSeedingToRoutes,
    isSeeding: readonly(isSeeding),
    currentSeedName: readonly(currentSeedName),
    routeHandlers,
  }
}
